import { User } from 'fitify-types/src/types/user'
import { isDefined } from 'fitify-utils/src/properties'

import { DEFAULT_PROMO_CODE } from 'data/promo'
import type { FunnelRoutesDefinition } from 'types/routes'
import { type DataPathState, Funnel, Question } from 'types/state'

import { getSubscriptionFlowPages } from '../../utils/funnel-pages'
import { getGymSectionDifficulty } from '../../utils/gym'

type GymF01DataState = DataPathState<Funnel.GYMF01>

const pages = {
  welcomeAgeBuckets: {
    path: '/gymf01/welcome-age-buckets',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    isExcludedFromProgressBarOrStepper: true,
    stateKey: 'age_bucket',
    id: 'welcome_age_buckets',
  },
  socialProofMillions: {
    path: '/gymf01/social-proof-millions',
    isValidatedStep: true,
    id: 'social_proof_millions',
  },
  experience: {
    path: '/gymf01/experience',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'experience',
    id: 'experience',
  },
  experiencePitch: {
    path: '/gymf01/experience-pitch',
    isValidatedStep: true,
    id: 'experience_pitch',
    condition: (data: GymF01DataState) => {
      return isDefined(data.experience)
    },
  },
  mainGoal: {
    path: '/gymf01/main-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'goal',
    id: 'main_goal',
    required: true,
  },
  goalPitch: {
    path: '/gymf01/goal-pitch',
    isValidatedStep: true,
    id: 'goal_pitch',
    condition: (data: GymF01DataState) => {
      return isDefined(data.goal)
    },
  },
  motivationEarly: {
    path: '/gymf01/motivation-early',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'motivation_early',
    id: 'motivation_early',
  },
  didYouKnowHormones: {
    path: '/gymf01/did-you-know-hormones',
    isValidatedStep: true,
    id: 'did_you_know_hormones',
  },
  didYouKnowDepression: {
    path: '/gymf01/did-you-know-depression',
    isValidatedStep: true,
    id: 'did_you_know_depression',
  },
  didYouKnowLongevity: {
    path: '/gymf01/did-you-know-longevity',
    isValidatedStep: true,
    id: 'did_you_know_longevity',
  },
  physicalBuild: {
    path: '/gymf01/physical-build',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'current_shape',
    id: 'physical_build',
  },
  bodyGoal: {
    path: '/gymf01/body-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'body_goal',
    id: 'body_goal',
  },
  weightChangeExp: {
    path: '/gymf01/weight-change-exp',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'weight_change_exp',
    id: 'weight_change_exp',
  },
  bodyTypePitch: {
    path: '/gymf01/body-type-pitch',
    isValidatedStep: true,
    id: 'body_type_pitch',
    condition: (data: GymF01DataState) => {
      return isDefined(data.weight_change_exp)
    },
  },
  bestShape: {
    path: '/gymf01/best-shape',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'latest_ideal_weight',
    id: 'best_shape',
  },
  flexible: {
    path: '/gymf01/flexible',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'flexible',
    id: 'flexible',
  },
  problemAreas: {
    path: '/gymf01/problem-areas',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'problem_areas',
    id: 'problem_areas',
    required: true,
  },
  problemAreasPitch: {
    path: '/gymf01/problem-areas-pitch',
    isValidatedStep: true,
    id: 'problem_areas_pitch',
  },
  struggle: {
    path: '/gymf01/struggle',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'struggle',
    id: 'struggle',
  },
  strugglePitch: {
    path: '/gymf01/struggle-pitch',
    isValidatedStep: true,
    id: 'struggle_pitch',
    condition: (data: GymF01DataState) => {
      return Boolean(
        data.struggle?.length &&
        data.struggle.some((item) => item !== Question.Struggle.None)
      )
    },
  },
  workoutLocation: {
    path: '/gymf01/workout-location',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_location',
    id: 'workout_location',
  },
  gymType: {
    path: '/gymf01/gym-type',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'gym_type',
    id: 'gym_type',
    condition: (data: GymF01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.Gym ||
        data.workout_location === Question.WorkoutLocation.Both ||
        data.workout_location === Question.WorkoutLocation.DoNotKnow
      )
    },
  },
  equipmentPreference: {
    path: '/gymf01/equipment-preference',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'equipment_preference',
    id: 'equipment_preference',
    condition: (data: GymF01DataState) => {
      return data.workout_location === Question.WorkoutLocation.Gym
    },
  },
  gymConfidence: {
    path: '/gymf01/gym-confidence',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'gym_confidence',
    id: 'gym_confidence',
    condition: (data: GymF01DataState) => {
      return data.workout_location === Question.WorkoutLocation.Gym
    },
  },
  gymConfidencePitch: {
    path: '/gymf01/gym-confidence-pitch',
    isValidatedStep: true,
    id: 'gym_confidence_pitch',
    condition: (data: GymF01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.Gym &&
        isDefined(data.gym_confidence) &&
        data.gym_confidence !== Question.GymConfidence.Confident
      )
    },
  },
  homeGym: {
    path: '/gymf01/home-gym',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'home_gym',
    id: 'home_gym',
    condition: (data: GymF01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.HomeGym ||
        data.workout_location === Question.WorkoutLocation.Both ||
        data.workout_location === Question.WorkoutLocation.DoNotKnow
      )
    },
  },
  locationCombinationPitch: {
    path: '/gymf01/location-combination-pitch',
    isValidatedStep: true,
    id: 'location_combination_pitch',
    condition: (data: GymF01DataState) => {
      return data.workout_location === Question.WorkoutLocation.Both
    },
  },
  gymDifficulty: {
    path: '/gymf01/gym-difficulty',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'gym_difficulty',
    id: 'gym_difficulty',
    condition: (data: GymF01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.Gym ||
        data.workout_location === Question.WorkoutLocation.Both
      )
    },
  },
  homeDifficulty: {
    path: '/gymf01/home-difficulty',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'home_difficulty',
    id: 'home_difficulty',
    condition: (data: GymF01DataState) => {
      return (
        data.workout_location === Question.WorkoutLocation.HomeGym ||
        data.workout_location === Question.WorkoutLocation.DoNotKnow
      )
    },
  },
  difficultyPitch: {
    path: '/gymf01/difficulty-pitch',
    isValidatedStep: true,
    id: 'difficulty_pitch',
    condition: (data: GymF01DataState) => {
      const difficulty = getGymSectionDifficulty(data)
      return (
        isDefined(difficulty) &&
        difficulty !== Question.GymDifficulty.None &&
        difficulty !== Question.HomeDifficulty.None
      )
    },
  },
  stairsCardio: {
    path: '/gymf01/stairs-cardio',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'stairs_cardio',
    id: 'stairs_cardio',
  },
  howOftenExercise: {
    path: '/gymf01/how-often-exercise',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_exercise',
    id: 'how_often_exercise',
  },
  workoutLength: {
    path: '/gymf01/workout-length',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_length',
    id: 'workout_length',
  },
  bigResultsPitch: {
    path: '/gymf01/big-results-pitch',
    isValidatedStep: true,
    id: 'big_results_pitch',
  },
  howOftenWalks: {
    path: '/gymf01/how-often-walks',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_walks',
    id: 'how_often_walks',
  },
  fitnessLevelSquats: {
    path: '/gymf01/fitness-level-squats',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_squats',
    id: 'fitness_level_squats',
  },
  fitnessLevelCrunches: {
    path: '/gymf01/fitness-level-crunches',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_crunches',
    id: 'fitness_level_crunches',
  },
  fitnessLevelPlank: {
    path: '/gymf01/fitness-level-plank',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'fitness_level_plank',
    id: 'fitness_level_plank',
  },
  eligibilityPitch: {
    path: '/gymf01/eligibility-pitch',
    isValidatedStep: true,
    id: 'eligibility_pitch',
  },
  workSchedule: {
    path: '/gymf01/work-schedule',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'work_schedule',
    id: 'work_schedule',
  },
  stress: {
    path: '/gymf01/stress',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'stress',
    id: 'stress',
  },
  cortisolPitch: {
    path: '/gymf01/cortisol-pitch',
    isValidatedStep: true,
    id: 'cortisol_pitch',
  },
  typicalDayActivity: {
    path: '/gymf01/typical-day-activity',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'typical_day_activity',
    id: 'typical_day_activity',
  },
  energyLevels: {
    path: '/gymf01/energy-levels',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'energy_levels',
    id: 'energy_levels',
  },
  energyPitch: {
    path: '/gymf01/energy-pitch',
    isValidatedStep: true,
    id: 'energy_pitch',
  },
  waterIntake: {
    path: '/gymf01/water-intake',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'water_intake',
    id: 'water_intake',
  },
  sleepDuration: {
    path: '/gymf01/sleep-duration',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'sleep_duration',
    id: 'sleep_duration',
  },
  timeBreakfast: {
    path: '/gymf01/time-breakfast',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_breakfast',
    id: 'time_breakfast',
  },
  timeLunch: {
    path: '/gymf01/time-lunch',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_lunch',
    id: 'time_lunch',
  },
  timeDinner: {
    path: '/gymf01/time-dinner',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_dinner',
    id: 'time_dinner',
  },
  diet: {
    path: '/gymf01/diet',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'diet',
    id: 'diet',
  },
  habits: {
    path: '/gymf01/habits',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'habits',
    id: 'habits',
  },
  cravings: {
    path: '/gymf01/cravings',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'cravings',
    id: 'cravings',
  },
  lifeEventsStopped: {
    path: '/gymf01/life-events-stopped',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_events_stopped',
    id: 'life_events_stopped',
  },
  mainMotivation: {
    path: '/gymf01/main-motivation',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'main_motivation',
    id: 'main_motivation',
  },
  height: {
    path: '/gymf01/height',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'height',
    id: 'height',
    required: true,
  },
  weight: {
    path: '/gymf01/weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'weight',
    id: 'weight',
    required: true,
  },
  goalWeight: {
    path: '/gymf01/goal-weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'goal_weight',
    id: 'goal_weight',
    required: true,
  },
  age: {
    path: '/gymf01/age',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'age',
    id: 'age',
    required: true,
  },
  wellnessProfile: {
    path: '/gymf01/wellness-profile',
    isValidatedStep: true,
    id: 'wellness_profile',
    condition: (data: GymF01DataState) => {
      return (
        isDefined(data.typical_day_activity) &&
        isDefined(data.current_shape) &&
        isDefined(data.how_often_exercise) &&
        isDefined(data.weight_change_exp)
      )
    },
  },
  lifeEvent: {
    path: '/gymf01/life-event',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_event',
    id: 'life_event',
  },
  lifeEventDate: {
    path: '/gymf01/life-event-date',
    isValidatedStep: true,
    stateKey: 'life_event_date',
    id: 'life_event_date',
    condition: (data: GymF01DataState) => {
      return (
        isDefined(data.life_event) &&
        data.life_event !== User.Profile.LifeEvent.NoEvent
      )
    },
  },
  lifeEventGraph: {
    path: '/gymf01/life-event-graph',
    isValidatedStep: true,
    id: 'life_event_graph',
    condition: (data: GymF01DataState) => {
      return isDefined(data.goal_weight) && isDefined(data.weight)
    },
  },
  credibilityPitch: {
    path: '/gymf01/credibility-pitch',
    isValidatedStep: true,
    id: 'credibility_pitch',
  },
  planGenerator: {
    path: '/gymf01/plan-generator',
    id: 'plan_generator',
  },
  email: {
    path: '/gymf01/email',
    id: 'email',
    stateKey: 'email',
  },
  name: {
    path: '/gymf01/name',
    isValidatedStep: true,
    stateKey: 'displayName',
    id: 'name',
  },
  namePitch: {
    path: '/gymf01/name-pitch',
    isValidatedStep: true,
    id: 'name_pitch',
  },
  discountReveal: {
    path: '/gymf01/discount-reveal',
    isValidatedStep: true,
    id: 'discount_reveal',
    condition: (_, { promoCode }) => {
      return Boolean(!promoCode || promoCode === DEFAULT_PROMO_CODE)
    },
  },
  ...getSubscriptionFlowPages(Funnel.GYMF01),
} as const satisfies FunnelRoutesDefinition<GymF01DataState>['pages']

const pagesMap = Object.values(pages)

const detectLocationPagesMap = [
  pages.planGenerator,
  pages.subscription,
  pages.exclusiveOffer,
] as const satisfies FunnelRoutesDefinition<GymF01DataState>['detectLocationPagesMap']

const noLeaveDialogPagesMap = [
  pages.subscription,
  pages.exclusiveOffer,
  pages.success,
] as const satisfies FunnelRoutesDefinition<GymF01DataState>['noLeaveDialogPagesMap']

export const GYMF01_ROUTES = {
  detectLocationPagesMap,
  noLeaveDialogPagesMap,
  pages,
  pagesMap,
} as const satisfies FunnelRoutesDefinition<GymF01DataState>
